import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Save, Eye, RefreshCw, Plus, Trash2, Image,
  Home, Star, Users, BookOpen, Megaphone, ChevronDown, ChevronUp
} from 'lucide-react';
import { isAdminAuthenticated, adminFetch } from '@/utils/adminAuth';
import { toast } from 'sonner';
import MediaLibraryPicker from '@/components/admin/MediaLibraryPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/20 rounded-lg">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-bold text-white">{title}</h3>
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-zinc-400" /> : <ChevronDown className="w-5 h-5 text-zinc-400" />}
      </button>
      
      {isOpen && (
        <div className="p-4 pt-0 border-t border-zinc-800">
          {children}
        </div>
      )}
    </div>
  );
};

// Input Field Component
const Field = ({ label, value, onChange, type = "text", placeholder = "", rows = 1 }) => (
  <div className="space-y-1">
    <label className="block text-xs font-bold text-zinc-400 uppercase">{label}</label>
    {type === "textarea" ? (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
      />
    ) : (
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
      />
    )}
  </div>
);

// Image Field Component
const ImageField = ({ label, value, onChange }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="space-y-1">
      <label className="block text-xs font-bold text-zinc-400 uppercase">{label}</label>
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Image URL..."
          className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button
          onClick={() => setShowPicker(true)}
          className="px-3 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
        >
          <Image className="w-4 h-4" />
        </button>
      </div>
      {value && (
        <div className="mt-2 relative w-32 h-20 rounded-lg overflow-hidden bg-zinc-800">
          <img loading="lazy" decoding="async" src={value.startsWith('http') ? value : `${BACKEND_URL}${value}`} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <MediaLibraryPicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(img) => {
          // Use the relative URL - frontend will resolve it
          onChange(img.url);
          setShowPicker(false);
        }}
        allowedTypes={['image']}
        multiple={false}
      />
    </div>
  );
};

export default function AdminHomeEditor() {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await adminFetch('/admin/home-content');
      const data = await response.json();
      setContent(data);
    } catch (error) {
      toast.error('Failed to load home page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await adminFetch('/admin/home-content', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(content)
      });

      if (response.ok) {
        toast.success('Home page saved!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save home page');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Are you sure you want to reset the home page to defaults?')) return;
    
    try {
      const response = await adminFetch('/admin/home-content/reset', {
        method: 'POST'
      });

      if (response.ok) {
        toast.success('Home page reset to defaults');
        loadContent();
      }
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSection = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateFeatureItem = (index, field, value) => {
    const newItems = [...content.features.items];
    newItems[index] = { ...newItems[index], [field]: value };
    updateSection('features', 'items', newItems);
  };

  const addFeatureItem = () => {
    const newItems = [...(content.features?.items || []), { title: '', description: '', icon: 'star' }];
    updateSection('features', 'items', newItems);
  };

  const removeFeatureItem = (index) => {
    const newItems = content.features.items.filter((_, i) => i !== index);
    updateSection('features', 'items', newItems);
  };

  const updateAchievement = (index, field, value) => {
    const newAchievements = [...content.mestreSection.achievements];
    newAchievements[index] = { ...newAchievements[index], [field]: value };
    updateSection('mestreSection', 'achievements', newAchievements);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading home page content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-background rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-foreground" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Home className="w-5 h-5 text-primary" />
                  Home Page Editor
                </h1>
                <p className="text-sm text-muted-foreground">Edit your home page content</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleReset}
                className="px-4 py-2 text-muted-foreground hover:text-foreground hover:bg-background rounded-lg transition-colors"
              >
                Reset to Defaults
              </button>
              
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 text-foreground hover:bg-background rounded-lg transition-colors"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        
        {/* Hero Section */}
        <Section title="Hero Section" icon={Home} defaultOpen={true}>
          <div className="space-y-4 mt-4">
            <Field 
              label="Badge Text" 
              value={content?.hero?.badge} 
              onChange={(v) => updateSection('hero', 'badge', v)}
              placeholder="38 YEARS OF AFRO-BRAZILIAN LEGACY"
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Main Title" 
                value={content?.hero?.title} 
                onChange={(v) => updateSection('hero', 'title', v)}
                placeholder="Casa Samba"
              />
              <Field 
                label="Tagline" 
                value={content?.hero?.tagline} 
                onChange={(v) => updateSection('hero', 'tagline', v)}
                placeholder="Learn. Play. Transform."
              />
            </div>
            <Field 
              label="Subtitle" 
              value={content?.hero?.subtitle} 
              onChange={(v) => updateSection('hero', 'subtitle', v)}
              placeholder="Enter the world of authentic Brasilian rhythm."
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Primary Button Text" 
                value={content?.hero?.primaryButtonText} 
                onChange={(v) => updateSection('hero', 'primaryButtonText', v)}
              />
              <Field 
                label="Primary Button Link" 
                value={content?.hero?.primaryButtonLink} 
                onChange={(v) => updateSection('hero', 'primaryButtonLink', v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Secondary Button Text" 
                value={content?.hero?.secondaryButtonText} 
                onChange={(v) => updateSection('hero', 'secondaryButtonText', v)}
              />
              <Field 
                label="Secondary Button Link" 
                value={content?.hero?.secondaryButtonLink} 
                onChange={(v) => updateSection('hero', 'secondaryButtonLink', v)}
              />
            </div>
            <ImageField 
              label="Background Image (optional)" 
              value={content?.hero?.backgroundImage} 
              onChange={(v) => updateSection('hero', 'backgroundImage', v)}
            />
          </div>
        </Section>

        {/* Features Section */}
        <Section title="Features Section" icon={Star}>
          <div className="space-y-4 mt-4">
            <Field 
              label="Section Title" 
              value={content?.features?.title} 
              onChange={(v) => updateSection('features', 'title', v)}
              placeholder="Why Casa Samba?"
            />
            <Field 
              label="Section Subtitle" 
              value={content?.features?.subtitle} 
              onChange={(v) => updateSection('features', 'subtitle', v)}
              placeholder="Three pillars of authentic Brasilian music education"
            />
            
            <div className="space-y-3">
              <label className="block text-xs font-bold text-zinc-400 uppercase">Feature Items</label>
              {(content?.features?.items || []).map((item, index) => (
                <div key={index} className="p-3 bg-zinc-800 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-zinc-400">Feature {index + 1}</span>
                    <button
                      onClick={() => removeFeatureItem(index)}
                      className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    value={item.title || ""}
                    onChange={(e) => updateFeatureItem(index, 'title', e.target.value)}
                    placeholder="Feature title"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <textarea
                    value={item.description || ""}
                    onChange={(e) => updateFeatureItem(index, 'description', e.target.value)}
                    placeholder="Feature description"
                    rows={2}
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                </div>
              ))}
              <button
                onClick={addFeatureItem}
                className="w-full py-2 border-2 border-dashed border-zinc-700 hover:border-primary/50 rounded-lg text-zinc-400 hover:text-primary flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </button>
            </div>
          </div>
        </Section>

        {/* Meet the Master Section */}
        <Section title="Meet the Master Section" icon={Users}>
          <div className="space-y-4 mt-4">
            <Field 
              label="Section Title" 
              value={content?.mestreSection?.title} 
              onChange={(v) => updateSection('mestreSection', 'title', v)}
              placeholder="Meet the Master"
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Name" 
                value={content?.mestreSection?.name} 
                onChange={(v) => updateSection('mestreSection', 'name', v)}
                placeholder="Mestre Curtis Pierre"
              />
              <Field 
                label="Subtitle" 
                value={content?.mestreSection?.subtitle} 
                onChange={(v) => updateSection('mestreSection', 'subtitle', v)}
                placeholder="38 Years of Afro-Brasilian Mastery"
              />
            </div>
            <Field 
              label="Description" 
              value={content?.mestreSection?.description} 
              onChange={(v) => updateSection('mestreSection', 'description', v)}
              type="textarea"
              rows={3}
            />
            <ImageField 
              label="Mestre Image" 
              value={content?.mestreSection?.image} 
              onChange={(v) => updateSection('mestreSection', 'image', v)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Button Text" 
                value={content?.mestreSection?.buttonText} 
                onChange={(v) => updateSection('mestreSection', 'buttonText', v)}
              />
              <Field 
                label="Button Link" 
                value={content?.mestreSection?.buttonLink} 
                onChange={(v) => updateSection('mestreSection', 'buttonLink', v)}
              />
            </div>
            
            <div className="space-y-2">
              <label className="block text-xs font-bold text-zinc-400 uppercase">Achievements</label>
              <div className="grid grid-cols-3 gap-3">
                {(content?.mestreSection?.achievements || []).map((ach, index) => (
                  <div key={index} className="p-3 bg-zinc-800 rounded-lg space-y-2">
                    <input
                      type="text"
                      value={ach.number || ""}
                      onChange={(e) => updateAchievement(index, 'number', e.target.value)}
                      placeholder="38+"
                      className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-white text-center font-bold"
                    />
                    <input
                      type="text"
                      value={ach.label || ""}
                      onChange={(e) => updateAchievement(index, 'label', e.target.value)}
                      placeholder="Years"
                      className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded text-white text-center text-xs"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>

        {/* Featured Course Section */}
        <Section title="Featured Course Section" icon={BookOpen}>
          <div className="space-y-4 mt-4">
            <Field 
              label="Section Title" 
              value={content?.coursesSection?.title} 
              onChange={(v) => updateSection('coursesSection', 'title', v)}
              placeholder="Featured Course"
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Course Title" 
                value={content?.coursesSection?.courseTitle} 
                onChange={(v) => updateSection('coursesSection', 'courseTitle', v)}
                placeholder="Pandeiro Masterclass"
              />
              <Field 
                label="Course Subtitle" 
                value={content?.coursesSection?.courseSubtitle} 
                onChange={(v) => updateSection('coursesSection', 'courseSubtitle', v)}
              />
            </div>
            <Field 
              label="Course Description" 
              value={content?.coursesSection?.courseDescription} 
              onChange={(v) => updateSection('coursesSection', 'courseDescription', v)}
              type="textarea"
              rows={2}
            />
            <ImageField 
              label="Course Image" 
              value={content?.coursesSection?.courseImage} 
              onChange={(v) => updateSection('coursesSection', 'courseImage', v)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Button Text" 
                value={content?.coursesSection?.buttonText} 
                onChange={(v) => updateSection('coursesSection', 'buttonText', v)}
              />
              <Field 
                label="Button Link" 
                value={content?.coursesSection?.buttonLink} 
                onChange={(v) => updateSection('coursesSection', 'buttonLink', v)}
              />
            </div>
          </div>
        </Section>

        {/* CTA Section */}
        <Section title="Call to Action Section" icon={Megaphone}>
          <div className="space-y-4 mt-4">
            <Field 
              label="Title" 
              value={content?.ctaSection?.title} 
              onChange={(v) => updateSection('ctaSection', 'title', v)}
              placeholder="Ready to Start Your Journey?"
            />
            <Field 
              label="Subtitle" 
              value={content?.ctaSection?.subtitle} 
              onChange={(v) => updateSection('ctaSection', 'subtitle', v)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Field 
                label="Button Text" 
                value={content?.ctaSection?.buttonText} 
                onChange={(v) => updateSection('ctaSection', 'buttonText', v)}
              />
              <Field 
                label="Button Link" 
                value={content?.ctaSection?.buttonLink} 
                onChange={(v) => updateSection('ctaSection', 'buttonLink', v)}
              />
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}
